import type {
  CourseAIOutlinePayload,
  CourseAIOutlineResult,
  CourseCreatePayload,
  CourseDetail,
  CourseListItem,
  CourseQuestion,
  CourseQuestionImportPayload,
  CourseUpdatePayload,
  OfficialCourseReviewCounts,
  OfficialCourseReviewStatus,
} from './courseType';
import { del, get, post, put } from '../request';

const LOCAL_TEACHER_PREVIEW_KEY = 'probfun_local_teacher_preview';
const LOCAL_ADMIN_PREVIEW_KEY = 'probfun_local_admin_preview';
const LOCAL_STUDENT_PREVIEW_KEY = 'probfun_local_student_preview';
const LOCAL_ADMIN_REVIEW_STATE_KEY = 'probfun_local_admin_course_review_state';

function isLocalTeacherPreview() {
  return import.meta.env.DEV && !localStorage.getItem('token') && sessionStorage.getItem(LOCAL_TEACHER_PREVIEW_KEY) === '1';
}

function isLocalAdminPreview() {
  return import.meta.env.DEV
    && !localStorage.getItem('token')
    && sessionStorage.getItem(LOCAL_ADMIN_PREVIEW_KEY) === '1';
}

function isLocalStudentPreview() {
  return import.meta.env.DEV && !localStorage.getItem('token') && sessionStorage.getItem(LOCAL_STUDENT_PREVIEW_KEY) === '1';
}

function buildPreviewCourse(): CourseDetail {
  const now = new Date().toISOString();
  return {
    id: 9001,
    name: '概率论教学示例',
    description: '用于预览课程状态、发布设置和班级绑定流程的本地教师课程。',
    chapter_count: 3,
    question_count: 24,
    bound_class_count: 2,
    bound_classes: [
      { id: 'preview-class-1', name: '2026 春 概率论 1 班', code: 'P2026A', student_count: 42 },
      { id: 'preview-class-2', name: '2026 春 概率论 2 班', code: 'P2026B', student_count: 39 },
    ],
    created_at: now,
    updated_at: now,
    is_published: false,
    published_at: null,
    teacher_name: '本地预览教师',
    max_courses: 8,
    chapters: [
      {
        id: 9101,
        name: '随机事件与概率',
        description: '事件关系、概率定义与基本性质。',
        order: 1,
        parent_id: null,
        question_count: 8,
        is_protected: false,
        knowledge_points: [
          { id: 9201, name: '随机事件', description: '', order: 1 },
          { id: 9202, name: '古典概型', description: '', order: 2 },
        ],
        children: [],
      },
      {
        id: 9102,
        name: '条件概率与独立性',
        description: '条件概率、全概率公式与贝叶斯公式。',
        order: 2,
        parent_id: null,
        question_count: 10,
        is_protected: false,
        knowledge_points: [
          { id: 9203, name: '条件概率', description: '', order: 1 },
          { id: 9204, name: '贝叶斯公式', description: '', order: 2 },
        ],
        children: [],
      },
      {
        id: 9103,
        name: '随机变量及其分布',
        description: '离散型与连续型随机变量。',
        order: 3,
        parent_id: null,
        question_count: 6,
        is_protected: false,
        knowledge_points: [
          { id: 9205, name: '分布函数', description: '', order: 1 },
          { id: 9206, name: '常见分布', description: '', order: 2 },
        ],
        children: [],
      },
    ],
  };
}

function buildPreviewCourseForId(courseId: number): CourseDetail {
  const course = buildPreviewCourse();
  if (courseId === course.id)
    return course;

  course.id = courseId;
  course.name = '线性代数发布示例';
  course.description = '展示已发布课程绑定班级后的课程工作区。';
  course.chapter_count = 4;
  course.question_count = 31;
  course.is_published = true;
  course.published_at = course.updated_at;
  course.bound_class_count = 1;
  course.bound_classes = [
    { id: 'preview-class-3', name: '2026 春 线性代数 1 班', code: 'L2026A', student_count: 36 },
  ];
  return course;
}

function loadLocalAdminReviewState(): Record<number, Partial<CourseListItem>> {
  try {
    return JSON.parse(sessionStorage.getItem(LOCAL_ADMIN_REVIEW_STATE_KEY) || '{}');
  }
  catch {
    return {};
  }
}

function saveLocalAdminReviewState(state: Record<number, Partial<CourseListItem>>) {
  sessionStorage.setItem(LOCAL_ADMIN_REVIEW_STATE_KEY, JSON.stringify(state));
}

function buildAdminReviewCourses(status: OfficialCourseReviewStatus | 'all' = 'pending') {
  const now = new Date().toISOString();
  const pending: CourseListItem[] = [
    {
      id: 9001,
      name: '概率论教学示例',
      description: '探索不确定世界的基础课程，包含概率模型、随机变量和常见分布。',
      chapter_count: 3,
      question_count: 24,
      bound_class_count: 2,
      created_at: now,
      updated_at: now,
      is_published: true,
      published_at: now,
      teacher_name: '李巧悦',
      official_review_status: 'pending',
      official_submitted_at: now,
    },
  ];
  const approved: CourseListItem[] = [
    {
      id: 9002,
      name: '线性代数发布示例',
      description: '空间与向量的基础课程，用于检查公开课程卡片展示。',
      chapter_count: 4,
      question_count: 31,
      bound_class_count: 1,
      created_at: now,
      updated_at: now,
      is_published: true,
      published_at: now,
      teacher_name: '本地预览教师',
      official_review_status: 'approved',
      official_submitted_at: now,
      official_reviewed_at: now,
      official_reviewed_by_name: '本地管理员',
    },
  ];
  const state = loadLocalAdminReviewState();
  const allCourses = [...pending, ...approved].map(course => ({ ...course, ...state[course.id] }));
  const courses = status === 'all' ? allCourses : allCourses.filter(course => course.official_review_status === status);
  const counts: OfficialCourseReviewCounts = {
    all: allCourses.length,
    pending: allCourses.filter(course => course.official_review_status === 'pending').length,
    approved: allCourses.filter(course => course.official_review_status === 'approved').length,
    rejected: allCourses.filter(course => course.official_review_status === 'rejected').length,
    delisted: allCourses.filter(course => course.official_review_status === 'delisted').length,
  };
  return { courses, counts };
}

async function createCourseApi(payload: CourseCreatePayload) {
  const result = await post<CourseDetail>('/assessment/course/create/', payload);
  return result.data;
}

async function parseCourseOutlineApi(payload: CourseAIOutlinePayload) {
  const formData = new FormData();
  formData.append('file', payload.file);
  if (payload.course_name)
    formData.append('course_name', payload.course_name);
  if (payload.description)
    formData.append('description', payload.description);
  formData.append('template_version', payload.template_version || 'v1');

  const result = await post<CourseAIOutlineResult>('/assessment/course/ai-outline/', formData, {
    timeout: 300000,
  });
  return result.data;
}

async function fetchMyCourseListApi() {
  if (isLocalStudentPreview()) {
    const now = new Date().toISOString();
    return {
      max_courses: 0,
      courses: [
        {
          id: 9002,
          name: '线性代数发布示例',
          description: '空间与向量的基础课程，老师已发布给你所在班级。',
          chapter_count: 4,
          question_count: 31,
          bound_class_count: 1,
          created_at: now,
          updated_at: now,
          is_published: true,
          published_at: now,
          teacher_name: '本地预览教师',
        },
        {
          id: 9001,
          name: '概率论教学示例',
          description: '概率模型、随机变量与常见分布的班级课程。',
          chapter_count: 3,
          question_count: 24,
          bound_class_count: 1,
          created_at: now,
          updated_at: now,
          is_published: true,
          published_at: now,
          teacher_name: '李巧悦',
        },
      ],
    };
  }

  if (isLocalTeacherPreview()) {
    const previewCourse = buildPreviewCourse();
    return {
      max_courses: 8,
      courses: [
        {
          id: previewCourse.id,
          name: previewCourse.name,
          description: previewCourse.description,
          chapter_count: previewCourse.chapter_count,
          question_count: previewCourse.question_count,
          bound_class_count: previewCourse.bound_class_count,
          created_at: previewCourse.created_at,
          updated_at: previewCourse.updated_at,
          is_published: previewCourse.is_published,
          published_at: previewCourse.published_at,
          teacher_name: previewCourse.teacher_name,
        },
        {
          id: 9002,
          name: '线性代数发布示例',
          description: '展示已发布课程在教师端列表中的状态。',
          chapter_count: 4,
          question_count: 31,
          bound_class_count: 1,
          created_at: previewCourse.created_at,
          updated_at: previewCourse.updated_at,
          is_published: true,
          published_at: previewCourse.updated_at,
          teacher_name: previewCourse.teacher_name,
          official_review_status: 'rejected' as const,
          official_review_reason: '简介过短，建议补充课程目标和适合人群后重新提交。',
          official_submitted_at: previewCourse.updated_at,
          official_reviewed_at: previewCourse.updated_at,
        },
      ],
    };
  }

  const result = await get<{
    courses: CourseListItem[];
    max_courses: number;
  }>('/assessment/course/list/');
  return result.data;
}

async function fetchCourseDetailApi(courseId: number) {
  if (isLocalTeacherPreview() || isLocalStudentPreview()) {
    const course = buildPreviewCourseForId(courseId);
    return { course };
  }

  const result = await get<{
    course: CourseDetail;
  }>(`/assessment/course/${courseId}/`);
  return result.data;
}

async function updateCourseApi(courseId: number, payload: CourseUpdatePayload) {
  if (isLocalTeacherPreview())
    return { course: { ...buildPreviewCourseForId(courseId), ...payload } };

  const result = await put<{
    course: CourseDetail;
  }>(`/assessment/course/${courseId}/update/`, payload);
  return result.data;
}

async function deleteCourseApi(courseId: number) {
  if (isLocalTeacherPreview())
    return { id: courseId };

  const result = await del<{
    id: number;
  }>(`/assessment/course/${courseId}/delete/`);
  return result.data;
}

async function publishCourseApi(courseId: number, action: 'publish' | 'unpublish') {
  if (isLocalTeacherPreview()) {
    const course = buildPreviewCourseForId(courseId);
    course.is_published = action === 'publish';
    course.published_at = action === 'publish' ? new Date().toISOString() : null;
    return { course };
  }

  const result = await post<{
    course: CourseDetail;
  }>(`/assessment/course/${courseId}/publish/`, { action });
  return result.data;
}

async function bindCourseClassesApi(courseId: number, classIds: Array<string | number>) {
  if (isLocalTeacherPreview()) {
    const course = buildPreviewCourseForId(courseId);
    course.bound_classes = classIds.map((id, index) => ({
      id,
      name: `本地预览班级 ${index + 1}`,
      code: `LOCAL${index + 1}`,
    }));
    course.bound_class_count = course.bound_classes.length;
    return { course };
  }

  const result = await post<{
    course: CourseDetail;
  }>(`/assessment/course/${courseId}/classes/`, { class_ids: classIds });
  return result.data;
}

async function fetchCourseQuestionBankApi(courseId: number, params?: { search?: string; type?: 'SC' | 'MC' | 'SA' | ''; limit?: number }) {
  const result = await get<{
    questions: CourseQuestion[];
  }>(`/assessment/course/${courseId}/question-bank/`, params);
  return result.data;
}

async function submitCourseOfficialReviewApi(courseId: number) {
  if (isLocalTeacherPreview()) {
    const course = buildPreviewCourseForId(courseId);
    course.official_review_status = 'pending';
    course.official_review_reason = '';
    course.official_submitted_at = new Date().toISOString();
    course.official_reviewed_at = null;
    return { course };
  }

  const result = await post<{
    course: CourseDetail;
  }>(`/assessment/course/${courseId}/official-review/`);
  return result.data;
}

async function cancelCourseOfficialReviewApi(courseId: number) {
  if (isLocalTeacherPreview()) {
    const course = buildPreviewCourseForId(courseId);
    course.is_published = true;
    course.published_at = course.updated_at;
    course.official_review_status = 'not_submitted';
    course.official_review_reason = '';
    course.official_submitted_at = null;
    course.official_reviewed_at = null;
    return { course };
  }

  const result = await post<{
    course: CourseDetail;
  }>(`/assessment/course/${courseId}/official-review/`, { action: 'cancel' });
  return result.data;
}

async function withdrawCourseOfficialReviewApi(courseId: number, reason = '') {
  if (isLocalTeacherPreview()) {
    const course = buildPreviewCourseForId(courseId);
    course.official_review_status = 'delisted';
    course.official_review_reason = reason || '教师主动取消公开';
    course.official_reviewed_at = new Date().toISOString();
    return { course };
  }

  const result = await post<{
    course: CourseDetail;
  }>(`/assessment/course/${courseId}/official-review/`, {
    action: 'withdraw',
    reason,
  });
  return result.data;
}

async function fetchOfficialCourseReviewListApi(status: OfficialCourseReviewStatus | 'all' = 'pending') {
  if (isLocalAdminPreview())
    return buildAdminReviewCourses(status);

  const result = await get<{
    courses: CourseListItem[];
    counts: OfficialCourseReviewCounts;
  }>('/assessment/course/official-reviews/', { status });
  return result.data;
}

async function fetchOfficialCourseReviewDetailApi(courseId: number) {
  if (isLocalAdminPreview()) {
    const state = loadLocalAdminReviewState();
    const course = buildPreviewCourse();
    course.id = courseId;
    course.is_published = true;
    course.teacher_name = courseId === 9001 ? '李巧悦' : '本地预览教师';
    course.official_review_status = courseId === 9001 ? 'pending' : 'approved';
    Object.assign(course, state[courseId]);
    return { course };
  }

  const result = await get<{
    course: CourseDetail;
  }>(`/assessment/course/official-reviews/${courseId}/`);
  return result.data;
}

async function reviewOfficialCourseApi(courseId: number, payload: { action: 'approve' | 'reject' | 'delist'; reason?: string }) {
  if (isLocalAdminPreview()) {
    const { course } = await fetchOfficialCourseReviewDetailApi(courseId);
    const state = loadLocalAdminReviewState();
    course.official_review_status = payload.action === 'approve'
      ? 'approved'
      : payload.action === 'delist'
        ? 'delisted'
        : 'rejected';
    course.official_review_reason = payload.action === 'reject' || payload.action === 'delist' ? payload.reason || '' : '';
    course.official_reviewed_at = new Date().toISOString();
    course.official_reviewed_by_name = '本地管理员';
    state[courseId] = {
      official_review_status: course.official_review_status,
      official_review_reason: course.official_review_reason,
      official_reviewed_at: course.official_reviewed_at,
      official_reviewed_by_name: course.official_reviewed_by_name,
    };
    saveLocalAdminReviewState(state);
    return { course };
  }

  const result = await post<{
    course: CourseDetail;
  }>(`/assessment/course/official-reviews/${courseId}/`, payload);
  return result.data;
}

async function fetchCourseChapterQuestionsApi(courseId: number, chapterId: number) {
  const result = await get<{
    questions: CourseQuestion[];
  }>(`/assessment/course/${courseId}/chapter/${chapterId}/questions/`);
  return result.data;
}

async function addCourseChapterQuestionsApi(courseId: number, chapterId: number, questionIds: string[]) {
  const result = await post<{
    questions: CourseQuestion[];
    course: CourseDetail;
  }>(`/assessment/course/${courseId}/chapter/${chapterId}/questions/`, {
    question_ids: questionIds,
  });
  return result.data;
}

async function removeCourseChapterQuestionsApi(courseId: number, chapterId: number, questionIds: string[]) {
  const result = await del<{
    deleted: number;
    course: CourseDetail;
  }>(`/assessment/course/${courseId}/chapter/${chapterId}/questions/`, {
    question_ids: questionIds,
  });
  return result.data;
}

async function importCourseChapterQuestionsApi(courseId: number, chapterId: number, payload: CourseQuestionImportPayload) {
  const result = await post<{
    questions: CourseQuestion[];
    course: CourseDetail;
  }>(`/assessment/course/${courseId}/chapter/${chapterId}/questions/`, payload);
  return result.data;
}

async function importCourseQuestionsApi(courseId: number, payload: CourseQuestionImportPayload) {
  const result = await post<{ questions: CourseQuestion[]; course: CourseDetail }>(
    `/assessment/course/${courseId}/questions/import/`,
    payload,
  );
  return result.data;
}

async function parseCourseQuestionSourceApi(courseId: number, file: File, signal?: AbortSignal) {
  const formData = new FormData();
  formData.append('file', file);
  const result = await post<{ markdown: string; filename: string; page_count: number }>(
    `/assessment/course/${courseId}/question-source/`,
    formData,
    { timeout: 300000, signal },
  );
  return result.data;
}

export {
  addCourseChapterQuestionsApi,
  bindCourseClassesApi,
  cancelCourseOfficialReviewApi,
  createCourseApi,
  deleteCourseApi,
  fetchCourseChapterQuestionsApi,
  fetchCourseDetailApi,
  fetchCourseQuestionBankApi,
  fetchMyCourseListApi,
  fetchOfficialCourseReviewDetailApi,
  fetchOfficialCourseReviewListApi,
  importCourseChapterQuestionsApi,
  importCourseQuestionsApi,
  parseCourseOutlineApi,
  parseCourseQuestionSourceApi,
  publishCourseApi,
  removeCourseChapterQuestionsApi,
  reviewOfficialCourseApi,
  submitCourseOfficialReviewApi,
  updateCourseApi,
  withdrawCourseOfficialReviewApi,
};
